import { captureFromCurrentWindow } from "@attribution/capture";
import { consumeInboundHandoff, stripAttributionParamsFromUrl } from "@attribution/handoff";
import { configureAttribution } from "@attribution/host";
import { createWebCryptoHmac } from "@attribution/webCryptoHmac";

/**
 * Caches the in-flight or completed boot promise for the current page load.
 * Guards against React StrictMode's dev-mode double effect invocation, which
 * would otherwise re-capture after stripAttributionParamsFromUrl has already
 * removed click ids from the URL — the second capture sees UTM-only, qualifies
 * as a newer recognized hit, and overwrites lastTouch.clickIds with an empty
 * object, dropping fbclid/gclid/etc. from every downstream handoff.
 *
 * Also allows downstream code (e.g. `stampSessionId` in `Funnel.tsx`) to
 * `await` the same promise so that the envelope written by
 * `captureFromCurrentWindow` is guaranteed to exist before they read it.
 */
let bootPromise: Promise<void> | undefined;

/**
 * The landing URL captured at the very start of the boot sequence, before
 * `stripAttributionParamsFromUrl` removes click ids and `ad_h` from the
 * address bar. GA4 is injected much later (post-strip), so gtag's
 * `page_location` is rebuilt from this snapshot to retain `click_id` et al.
 */
let landingHref: string | undefined;

/**
 * Returns the pre-strip landing URL recorded during `initializeAttribution`,
 * or `undefined` before the first boot.
 */
export const getLandingHref = (): string | undefined => landingHref;

/**
 * Exposed for tests and for the "Re-capture" demo button, which intentionally
 * wants to re-run the boot sequence against the current URL.
 */
export const resetAttributionBootGuard = (): void => {
  bootPromise = undefined;
  landingHref = undefined;
};

/**
 * Boot sequence: install the optional HMAC signer/verifier, consume any
 * inbound `?ad_h=`, capture the current URL into the envelope, then clean up
 * the address bar.
 *
 * Every step is independently guarded so a failure in one stage never cancels
 * the others — in particular the URL strip must always run.
 *
 * Idempotent within a single page load: repeat calls are no-ops until
 * `resetAttributionBootGuard` is invoked or the page reloads.
 */
export const initializeAttribution = (signingSecret: string | undefined): Promise<void> => {
  if (typeof window === "undefined") return Promise.resolve();
  if (bootPromise !== undefined) return bootPromise;
  bootPromise = runAttributionBoot(signingSecret);
  return bootPromise;
};

async function runAttributionBoot(signingSecret: string | undefined): Promise<void> {
  landingHref = window.location.href;

  if (signingSecret !== undefined && signingSecret !== "") {
    try {
      const secretBytes = new TextEncoder().encode(signingSecret);
      const hmac = await createWebCryptoHmac(secretBytes);
      if (hmac !== undefined) {
        configureAttribution({ signer: hmac.signer, verifier: hmac.verifier });
      }
    } catch {
      // Fall back to unsigned.
    }
  }

  try {
    await consumeInboundHandoff();
  } catch {
    // Still strip the URL below.
  }

  try {
    captureFromCurrentWindow();
  } catch {
    // Envelope may be stale; do not block the app.
  }

  try {
    stripAttributionParamsFromUrl();
  } catch {
    // history.replaceState may throw in restricted contexts (e.g. sandboxed
    // iframes, certain webview hosts). Attribution is already persisted;
    // the worst outcome is click-id params lingering in the URL.
  }
}
